import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getDispatches, getCustomers, markDispatched, openDispatchPdf, openPalletLabels, openCoaPdf, softDeleteDispatch, reverseDispatch, undoReverseDispatch } from '../lib/api';
import AldiBranchDialog from '../components/AldiBranchDialog';
import RecordReturnDialog from '../components/RecordReturnDialog';
import DispatchNotePurgeDialog from '../components/DispatchNotePurgeDialog';
import { InvoicePreviewDialog } from '../components/accountsiq/InvoicePreviewDialog';
import { formatDate, getStatusColor } from '../lib/utils';
import { openPrintWindow } from '../lib/printWindow';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  FileText, 
  MoreVertical, 
  Truck,
  Eye,
  Loader2,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Trash2,
  AlertTriangle,
  Package,
  Download,
  Printer,
  ScrollText,
  Undo2,
  RotateCcw,
  ShieldAlert,
  Send
} from 'lucide-react';
import { DatePicker } from '../components/ui/date-picker';
import { 
  format, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth,
  endOfMonth,
  addWeeks, 
  subWeeks, 
  addMonths,
  subMonths,
  getWeek, 
  getYear, 
  isWithinInterval, 
  parse
} from 'date-fns';

const DELETION_REASONS = [
  { value: 'duplicate', label: 'Duplicate Entry' },
  { value: 'error', label: 'Data Error' },
  { value: 'cancelled', label: 'Order Cancelled' },
  { value: 'customer_request', label: 'Customer Request' },
  { value: 'other', label: 'Other' },
];

export default function Dispatches() {
  const navigate = useNavigate();
  const [dispatches, setDispatches] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all'); // all, week, month, year
  const [currentDate, setCurrentDate] = useState(new Date());
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [aldiBranchOpen, setAldiBranchOpen] = useState(false);
  const [aldiBranchDispatchId, setAldiBranchDispatchId] = useState(null);
  const [dispatchToDelete, setDispatchToDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteNotes, setDeleteNotes] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Uplift date dialog
  const [upliftDialogOpen, setUpliftDialogOpen] = useState(false);
  const [upliftDate, setUpliftDate] = useState(null);
  const [upliftDispatchRef, setUpliftDispatchRef] = useState(null);

  // Stock Return + Reverse Dispatch dialogs
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnDispatch, setReturnDispatch] = useState(null);
  const [aiqPreviewDispatch, setAiqPreviewDispatch] = useState(null);
  const [reverseDialogOpen, setReverseDialogOpen] = useState(false);
  const [reverseTarget, setReverseTarget] = useState(null);
  const [reverseReason, setReverseReason] = useState('');
  const [reversing, setReversing] = useState(false);
  const [undoTarget, setUndoTarget] = useState(null);
  const [undoReason, setUndoReason] = useState('');
  const [undoing, setUndoing] = useState(false);
  // Dispatch note purge (standalone notes raised in error)
  const [purgeTarget, setPurgeTarget] = useState(null);

  const { user: authUser } = useAuth();
  const isAdmin = ['super_admin', 'admin'].includes(authUser?.role);

  const handleUndoReverse = async () => {
    if (!undoTarget) return;
    if (undoReason.trim().length < 5) {
      toast.error('Enter a reason (min 5 characters).');
      return;
    }
    setUndoing(true);
    try {
      const res = await undoReverseDispatch(undoTarget.id, { reason: undoReason.trim() });
      toast.success(res.data.message || 'Reversal undone');
      setUndoTarget(null);
      setUndoReason('');
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to undo reversal');
    } finally {
      setUndoing(false);
    }
  };

  const handleReverse = async () => {
    if (!reverseTarget) return;
    if (reverseReason.trim().length < 5) {
      toast.error('Enter a reason (min 5 characters).');
      return;
    }
    setReversing(true);
    try {
      const res = await reverseDispatch(reverseTarget.id, { reason: reverseReason.trim() });
      toast.success(res.data.message || 'Dispatch reversed');
      setReverseDialogOpen(false);
      setReverseTarget(null);
      setReverseReason('');
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reverse dispatch');
    } finally {
      setReversing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const [dispatchesRes, customersRes] = await Promise.all([
        getDispatches(params),
        getCustomers()
      ]);
      setDispatches(dispatchesRes.data || []);
      setCustomers(customersRes.data || []);
    } catch (error) {
      toast.error('Failed to load dispatches');
    } finally {
      setLoading(false);
    }
  };

  const loadDispatches = loadData; // Alias for compatibility

  // Date range for filtering
  const dateRange = useMemo(() => {
    if (dateFilter === 'week') {
      return { 
        start: startOfWeek(currentDate, { weekStartsOn: 1 }), 
        end: endOfWeek(currentDate, { weekStartsOn: 1 }) 
      };
    } else if (dateFilter === 'month') {
      return { 
        start: startOfMonth(currentDate), 
        end: endOfMonth(currentDate) 
      };
    } else if (dateFilter === 'year') {
      const year = getYear(currentDate);
      return { 
        start: new Date(year, 0, 1), 
        end: new Date(year, 11, 31) 
      };
    }
    return null;
  }, [dateFilter, currentDate]);

  const weekNumber = getWeek(currentDate, { weekStartsOn: 1 });
  const year = getYear(currentDate);
  const monthName = format(currentDate, 'MMMM yyyy');
  const yearName = format(currentDate, 'yyyy');

  // Filter dispatches
  const filteredDispatches = useMemo(() => {
    let result = dispatches;
    
    // Customer filter
    if (customerFilter && customerFilter !== 'all') {
      result = result.filter(d => d.customer_id === customerFilter);
    }
    
    // Search filter (by customer name, PO number, dispatch number, supplier product code, batch code)
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(d => {
        // Check dispatch-level fields
        if (d.customer_name?.toLowerCase().includes(searchLower)) return true;
        if (d.po_number?.toLowerCase().includes(searchLower)) return true;
        if (d.dispatch_number?.toLowerCase().includes(searchLower)) return true;
        
        // Check line-level fields (supplier product code, batch code)
        if (d.lines && d.lines.length > 0) {
          return d.lines.some(line => 
            line.supplier_product_code?.toLowerCase().includes(searchLower) ||
            line.batch_code?.toLowerCase().includes(searchLower) ||
            line.product_name?.toLowerCase().includes(searchLower)
          );
        }
        return false;
      });
    }
    
    // Date filter
    if (dateRange) {
      result = result.filter(d => {
        try {
          const dispatchDate = d.dispatch_date 
            ? parse(d.dispatch_date, 'dd/MM/yyyy', new Date())
            : new Date(d.created_at);
          return isWithinInterval(dispatchDate, dateRange);
        } catch {
          return true;
        }
      });
    }
    
    return result;
  }, [dispatches, customerFilter, search, dateRange]);

  // Pagination
  const totalPages = pageSize === 'all' ? 1 : Math.ceil(filteredDispatches.length / pageSize);
  const paginatedDispatches = useMemo(() => {
    if (pageSize === 'all') return filteredDispatches;
    const start = (currentPage - 1) * pageSize;
    return filteredDispatches.slice(start, start + pageSize);
  }, [filteredDispatches, currentPage, pageSize]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, customerFilter, statusFilter, dateFilter]);

  // Export to CSV
  const exportToCSV = () => {
    if (filteredDispatches.length === 0) {
      toast.error('No dispatches to export');
      return;
    }
    
    const headers = ['Dispatch #', 'PO Number', 'Customer', 'Status', 'Dispatch Date', 'Total Units', 'Total Weight (kg)', 'Total Cases', 'Created'];
    const rows = filteredDispatches.map(d => {
      // Per-line: production override → batch total → estimated. Cases: override → calc → ordered
      const lineWeight = (l) => l.weight_override || l.total_weight || l.estimated_weight || 0;
      const lineCases = (l) => l.cases_override || l.calculated_cases || l.cases || 0;
      const totalUnits = d.lines?.reduce((s, l) => s + (l.quantity || 0), 0) || 0;
      const totalWeight = d.total_net_weight || d.total_ordered_weight ||
        (d.lines?.reduce((s, l) => s + lineWeight(l), 0) || 0);
      const totalCases = d.total_cases || d.total_ordered_cases ||
        (d.lines?.reduce((s, l) => s + lineCases(l), 0) || 0);
      return [
        d.dispatch_number,
        d.po_number || '',
        d.customer_name || '',
        d.status,
        d.dispatch_date || '',
        totalUnits,
        Number(totalWeight).toFixed(2),
        Number(totalCases).toFixed(1) === '0.0' ? '0' : String(Math.ceil(Number(totalCases) - 1e-6)),
        d.created_at ? format(new Date(d.created_at), 'dd/MM/yyyy HH:mm') : ''
      ];
    });
    
    const csvContent = [headers.join(','), ...rows.map(r => r.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dispatches_${format(new Date(), 'dd-MM-yyyy')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filteredDispatches.length} dispatches to CSV`);
  };

  // Print list
  const printList = () => {
    if (filteredDispatches.length === 0) {
      toast.error('No dispatches to print');
      return;
    }
    
    const filterDesc = dateFilter === 'week' ? `Week ${weekNumber} ${year}` 
      : dateFilter === 'month' ? monthName 
      : dateFilter === 'year' ? yearName 
      : 'All Dates';
    const customerDesc = customerFilter && customerFilter !== 'all' 
      ? customers.find(c => c.id === customerFilter)?.name || '' 
      : 'All Customers';
    
    openPrintWindow(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Dispatch List</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; font-size: 11px; }
          h1 { font-size: 16px; margin-bottom: 5px; }
          .subtitle { color: #666; margin-bottom: 15px; font-size: 12px; }
          .filters { background: #f5f5f5; padding: 8px 12px; border-radius: 4px; margin-bottom: 15px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th, td { border: 1px solid #ccc; padding: 5px 8px; text-align: left; }
          th { background: #f5f5f5; font-weight: 600; }
          .mono { font-family: monospace; }
          .right { text-align: right; }
          .center { text-align: center; }
          .status-draft { background: #dbeafe; color: #1d4ed8; }
          .status-in_progress { background: #fef3c7; color: #d97706; }
          .status-locked { background: #d1fae5; color: #047857; }
          .status-dispatched { background: #e0e7ff; color: #4338ca; }
          .totals { margin-top: 15px; font-weight: 600; }
          @media print { @page { size: landscape; margin: 10mm; } }
        </style>
      </head>
      <body>
        <h1>Dispatch List Report</h1>
        <div class="subtitle">Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm')}</div>
        <div class="filters">
          <strong>Filters:</strong> ${filterDesc} | ${customerDesc} | ${statusFilter === 'all' ? 'All Statuses' : statusFilter} | ${filteredDispatches.length} dispatches
        </div>
        <table>
          <thead>
            <tr>
              <th>Dispatch #</th>
              <th>PO Number</th>
              <th>Customer</th>
              <th class="center">Status</th>
              <th>Dispatch Date</th>
              <th class="right">Units</th>
              <th class="right">Weight</th>
              <th class="right">Cases</th>
            </tr>
          </thead>
          <tbody>
            ${filteredDispatches.map(d => {
              const lineWeight = (l) => l.weight_override || l.total_weight || l.estimated_weight || 0;
              const lineCases = (l) => l.cases_override || l.calculated_cases || l.cases || 0;
              const totalUnits = d.lines?.reduce((s, l) => s + (l.quantity || 0), 0) || 0;
              const totalWeight = d.total_net_weight || d.total_ordered_weight ||
                (d.lines?.reduce((s, l) => s + lineWeight(l), 0) || 0);
              const totalCases = d.total_cases || d.total_ordered_cases ||
                (d.lines?.reduce((s, l) => s + lineCases(l), 0) || 0);
              return `
              <tr>
                <td class="mono">${d.dispatch_number}</td>
                <td class="mono">${d.po_number || '-'}</td>
                <td>${d.customer_name || ''}</td>
                <td class="center status-${d.status}">${d.status.replace('_', ' ')}</td>
                <td class="mono">${d.dispatch_date || '-'}</td>
                <td class="right mono">${totalUnits}</td>
                <td class="right mono">${Number(totalWeight).toFixed(2)}</td>
                <td class="right mono">${Math.ceil(Number(totalCases) - 1e-6) || 0}</td>
              </tr>
            `;}).join('')}
          </tbody>
        </table>
        <div class="totals">
          Totals: ${filteredDispatches.reduce((sum, d) => sum + (d.lines?.reduce((s, l) => s + (l.quantity || 0), 0) || 0), 0)} units | 
          ${filteredDispatches.reduce((sum, d) => {
            const lw = (l) => l.weight_override || l.total_weight || l.estimated_weight || 0;
            return sum + (d.total_net_weight || d.total_ordered_weight || (d.lines?.reduce((s, l) => s + lw(l), 0) || 0));
          }, 0).toFixed(2)} kg | 
          ${filteredDispatches.reduce((sum, d) => {
            const lc = (l) => l.cases_override || l.calculated_cases || l.cases || 0;
            return sum + (d.total_cases || d.total_ordered_cases || (d.lines?.reduce((s, l) => s + lc(l), 0) || 0));
          }, 0).toFixed(1)} cases
        </div>
      </body>
      </html>
    `);
  };

  const goToPrevious = () => {
    if (dateFilter === 'week') setCurrentDate(subWeeks(currentDate, 1));
    else if (dateFilter === 'month') setCurrentDate(subMonths(currentDate, 1));
    else if (dateFilter === 'year') setCurrentDate(new Date(getYear(currentDate) - 1, 0, 1));
  };

  const goToNext = () => {
    if (dateFilter === 'week') setCurrentDate(addWeeks(currentDate, 1));
    else if (dateFilter === 'month') setCurrentDate(addMonths(currentDate, 1));
    else if (dateFilter === 'year') setCurrentDate(new Date(getYear(currentDate) + 1, 0, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleMarkDispatched = async (dispatch) => {
    setUpliftDispatchRef(dispatch);
    setUpliftDate(dispatch.dispatch_date || null);
    setUpliftDialogOpen(true);
  };

  const confirmMarkDispatched = async () => {
    if (!upliftDispatchRef) return;
    try {
      await markDispatched(upliftDispatchRef.id, upliftDate || undefined);
      toast.success('Dispatch marked as dispatched');
      setUpliftDialogOpen(false);
      setUpliftDispatchRef(null);
      loadDispatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update dispatch');
    }
  };

  const handleViewPDF = async (id) => {
    await openDispatchPdf(id);
  };

  const openDeleteDialog = (dispatch) => {
    setDispatchToDelete(dispatch);
    setDeleteReason('');
    setDeleteNotes('');
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!dispatchToDelete || !deleteReason) {
      toast.error('Please select a deletion reason');
      return;
    }
    
    setDeleting(true);
    try {
      await softDeleteDispatch(dispatchToDelete.id, {
        reason: deleteReason,
        notes: deleteNotes
      });
      toast.success(`Dispatch ${dispatchToDelete.dispatch_number} deleted`);
      setDeleteDialogOpen(false);
      setDispatchToDelete(null);
      loadDispatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete dispatch');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      in_progress: 'bg-amber-100 text-amber-700',
      locked: 'bg-blue-100 text-blue-700',
      dispatched: 'bg-emerald-100 text-emerald-700',
      cancelled: 'bg-red-100 text-red-700',
      reversed: 'bg-red-100 text-red-700',
    };
    const labels = {
      draft: 'Draft',
      in_progress: 'In Progress',
      locked: 'Locked',
      dispatched: 'Dispatched',
      cancelled: 'Cancelled',
      reversed: 'Reversed',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || colors.draft}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getDateFilterLabel = () => {
    if (dateFilter === 'week') return `Week ${weekNumber}, ${year}`;
    if (dateFilter === 'month') return monthName;
    if (dateFilter === 'year') return yearName;
    return 'All Time';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dispatches-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Truck className="w-5 h-5" />
            Dispatches
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {filteredDispatches.length} dispatch{filteredDispatches.length !== 1 ? 'es' : ''} found
          </p>
        </div>
        <div className="flex gap-2">
          {/* Export buttons */}
          {filteredDispatches.length > 0 && (
            <>
              <Button variant="outline" size="sm" onClick={exportToCSV} title="Export to CSV">
                <Download className="w-4 h-4 mr-1" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={printList} title="Print List">
                <Printer className="w-4 h-4 mr-1" />
                Print
              </Button>
            </>
          )}
          <Button onClick={() => navigate('/dispatch/new')} data-testid="btn-new-dispatch">
            <Plus className="w-4 h-4 mr-2" />
            New Dispatch
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by dispatch #, customer, PO, product, customer code, batch..."
                className="pl-10"
                data-testid="input-search"
              />
            </div>
            
            {/* Customer Filter */}
            <Select value={customerFilter} onValueChange={setCustomerFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Customers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Customers</SelectItem>
                {customers.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="locked">Locked</SelectItem>
                <SelectItem value="dispatched">Dispatched</SelectItem>
                <SelectItem value="reversed">Reversed</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Date Filter */}
            <Select value={dateFilter} onValueChange={setDateFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Date Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="week">By Week</SelectItem>
                <SelectItem value="month">By Month</SelectItem>
                <SelectItem value="year">By Year</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Date Navigation */}
          {dateFilter !== 'all' && (
            <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t">
              <Button variant="outline" size="sm" onClick={goToPrevious}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <div className="text-center min-w-[150px]">
                <div className="font-medium">{getDateFilterLabel()}</div>
                {dateFilter === 'week' && dateRange && (
                  <div className="text-xs text-muted-foreground">
                    {format(dateRange.start, 'dd MMM')} - {format(dateRange.end, 'dd MMM')}
                  </div>
                )}
              </div>
              
              <Button variant="outline" size="sm" onClick={goToNext}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              
              <Button variant="link" size="sm" onClick={goToToday} className="text-xs">
                Today
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results Count with Pagination Controls */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          Showing {paginatedDispatches.length} of {filteredDispatches.length} dispatches
          {pageSize !== 'all' && totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Show:</span>
          <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(v === 'all' ? 'all' : parseInt(v)); setCurrentPage(1); }}>
            <SelectTrigger className="w-20 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          {pageSize !== 'all' && totalPages > 1 && (
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Dispatches Table */}
      {paginatedDispatches.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Dispatch #</th>
                    <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Customer</th>
                    <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">PO #</th>
                    <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Products</th>
                    <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Cases</th>
                    <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Weight</th>
                    <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Status</th>
                    <th className="w-24"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {paginatedDispatches.map((dispatch) => {
                    const productNames = dispatch.lines?.map(l => l.product_name).filter(Boolean) || [];
                    const productDisplay = productNames.length > 2 
                      ? `${productNames.slice(0, 2).join(', ')} +${productNames.length - 2}`
                      : productNames.join(', ') || '-';
                    
                    return (
                      <tr 
                        key={dispatch.id} 
                        className="hover:bg-muted/30 cursor-pointer"
                        onClick={() => navigate(`/dispatch/${dispatch.id}`)}
                        data-testid={`dispatch-row-${dispatch.id}`}
                      >
                        <td className="px-4 py-3 font-mono font-medium">{dispatch.dispatch_number}</td>
                        <td className="px-4 py-3">
                          {dispatch.customer_name}
                          {dispatch.destination_type === 'event' && (
                            <Badge variant="outline" className="ml-2 bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]" data-testid={`event-badge-${dispatch.dispatch_number}`}>
                              EVENT
                            </Badge>
                          )}
                          {dispatch.has_returns && dispatch.status !== 'reversed' && (
                            <Badge variant="outline" className="ml-2 bg-amber-100 text-amber-700 border-amber-200 text-[10px]" data-testid={`returns-badge-${dispatch.dispatch_number}`}>
                              RETURNS
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-sm">{dispatch.po_number || '-'}</td>
                        <td className="px-4 py-3 font-mono text-sm">{dispatch.dispatch_date}</td>
                        <td className="px-4 py-3 text-sm max-w-[200px] truncate" title={productNames.join(', ')}>
                          {productDisplay}
                        </td>
                        <td className="px-4 py-3 font-mono text-sm text-right">{Math.ceil(dispatch.total_cases || 0) || '0'}</td>
                        <td className="px-4 py-3 font-mono text-sm text-right">{dispatch.total_net_weight?.toFixed(1) || '0'} kg</td>
                        <td className="px-4 py-3">{getStatusBadge(dispatch.status)}</td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`dispatch-menu-${dispatch.dispatch_number}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/dispatch/${dispatch.id}`)}>
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              {dispatch.status === 'locked' && (
                                <DropdownMenuItem onClick={() => handleViewPDF(dispatch.id)}>
                                  <FileText className="w-4 h-4 mr-2" />
                                  View PDF
                                </DropdownMenuItem>
                              )}
                              {dispatch.status === 'locked' && (
                                <DropdownMenuItem onClick={() => handleMarkDispatched(dispatch)}>
                                  <Truck className="w-4 h-4 mr-2" />
                                  Mark Dispatched
                                </DropdownMenuItem>
                              )}
                              {(dispatch.status === 'locked' || dispatch.status === 'dispatched') && (
                                <DropdownMenuSub>
                                  <DropdownMenuSubTrigger>
                                    <Package className="w-4 h-4 mr-2" />
                                    Print Pallet Label
                                  </DropdownMenuSubTrigger>
                                  <DropdownMenuSubContent>
                                    <DropdownMenuItem onClick={() => navigate(`/pallet-labels/${dispatch.id}`)}>
                                      <FileText className="w-4 h-4 mr-2" />
                                      Standard (Designer)
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openPalletLabels(dispatch.id, 2, { template: 'agropur' })}>
                                      <Package className="w-4 h-4 mr-2" />
                                      Agropur
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => {
                                      setAldiBranchDispatchId(dispatch.id);
                                      setAldiBranchOpen(true);
                                    }}>
                                      <Package className="w-4 h-4 mr-2" />
                                      Aldi
                                    </DropdownMenuItem>
                                  </DropdownMenuSubContent>
                                </DropdownMenuSub>
                              )}
                              {(dispatch.status === 'locked' || dispatch.status === 'dispatched') && (
                                <DropdownMenuItem
                                  onClick={() => openCoaPdf(dispatch.id)}
                                  data-testid={`coa-pdf-${dispatch.id}`}
                                >
                                  <ScrollText className="w-4 h-4 mr-2" />
                                  Certificate of Analysis
                                </DropdownMenuItem>
                              )}
                              {dispatch.status === 'dispatched' && dispatch.destination_type !== 'event' && (
                                <DropdownMenuItem
                                  onClick={() => setAiqPreviewDispatch(dispatch)}
                                  data-testid={`aiq-preview-${dispatch.dispatch_number}`}
                                >
                                  <Send className="w-4 h-4 mr-2 text-[#005F73]" />
                                  {dispatch.accountsiq_invoice_id ? `AccountsIQ draft #${dispatch.accountsiq_invoice_id}` : 'Preview & send to AccountsIQ'}
                                </DropdownMenuItem>
                              )}
                              {['ready', 'locked', 'dispatched', 'completed'].includes(dispatch.status) && (
                                <DropdownMenuItem
                                  onClick={() => { setReturnDispatch(dispatch); setReturnDialogOpen(true); }}
                                  data-testid={`record-return-${dispatch.dispatch_number}`}
                                >
                                  <Undo2 className="w-4 h-4 mr-2" />
                                  Record Return
                                </DropdownMenuItem>
                              )}
                              {['ready', 'locked', 'dispatched'].includes(dispatch.status) && dispatch.destination_type !== 'event' && (
                                <DropdownMenuItem
                                  onClick={() => { setReverseTarget(dispatch); setReverseReason(''); setReverseDialogOpen(true); }}
                                  className="text-red-600 focus:text-red-600"
                                  data-testid={`reverse-dispatch-${dispatch.dispatch_number}`}
                                >
                                  <RotateCcw className="w-4 h-4 mr-2" />
                                  Reverse Dispatch (cancelled)
                                </DropdownMenuItem>
                              )}
                              {dispatch.status === 'reversed' && isAdmin && (
                                <DropdownMenuItem
                                  onClick={() => { setUndoTarget(dispatch); setUndoReason(''); }}
                                  data-testid={`undo-reverse-${dispatch.dispatch_number}`}
                                >
                                  <RotateCcw className="w-4 h-4 mr-2" />
                                  Undo Reversal (admin)
                                </DropdownMenuItem>
                              )}
                              {isAdmin && !(dispatch.po_number || '').trim() && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => setPurgeTarget(dispatch)}
                                    className="text-orange-700 focus:text-orange-700"
                                    data-testid={`purge-note-${dispatch.dispatch_number}`}
                                  >
                                    <ShieldAlert className="w-4 h-4 mr-2" />
                                    Purge Dispatch Note
                                  </DropdownMenuItem>
                                </>
                              )}
                              {dispatch.status !== 'dispatched' && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    onClick={() => openDeleteDialog(dispatch)}
                                    className="text-red-600 focus:text-red-600"
                                    data-testid={`delete-dispatch-${dispatch.id}`}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Truck className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-medium mb-2">No dispatches found</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {search ? 'Try adjusting your search or filters' : 'Create your first dispatch to get started'}
            </p>
            {!search && (
              <Button onClick={() => navigate('/dispatch/new')}>
                <Plus className="w-4 h-4 mr-2" />
                New Dispatch
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Delete Dispatch
            </DialogTitle>
            <DialogDescription>
              This action will soft-delete the dispatch. It can be restored from the Deleted Items page.
            </DialogDescription>
          </DialogHeader>
          
          {dispatchToDelete && (
            <div className="py-4 space-y-4">
              {/* Dispatch Info */}
              <div className="bg-muted/50 p-3 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Dispatch #:</span>
                  <span className="font-mono font-medium">{dispatchToDelete.dispatch_number}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Customer:</span>
                  <span className="font-medium">{dispatchToDelete.customer_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Date:</span>
                  <span className="font-mono">{dispatchToDelete.dispatch_date}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Status:</span>
                  {getStatusBadge(dispatchToDelete.status)}
                </div>
              </div>
              
              {/* Deletion Reason */}
              <div className="space-y-2">
                <Label htmlFor="deleteReason">Reason for deletion *</Label>
                <Select value={deleteReason} onValueChange={setDeleteReason}>
                  <SelectTrigger id="deleteReason" data-testid="select-delete-reason">
                    <SelectValue placeholder="Select a reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    {DELETION_REASONS.map((reason) => (
                      <SelectItem key={reason.value} value={reason.value}>
                        {reason.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Optional Notes */}
              <div className="space-y-2">
                <Label htmlFor="deleteNotes">Additional notes (optional)</Label>
                <Textarea
                  id="deleteNotes"
                  value={deleteNotes}
                  onChange={(e) => setDeleteNotes(e.target.value)}
                  placeholder="Add any additional details..."
                  rows={2}
                  data-testid="input-delete-notes"
                />
              </div>
              
              {/* Warning */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                <strong>Note:</strong> Any stock allocated to this dispatch will be released back to the batches.
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete} 
              disabled={deleting || !deleteReason}
              data-testid="btn-confirm-delete"
            >
              {deleting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" /> Delete Dispatch</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DispatchNotePurgeDialog
        open={!!purgeTarget}
        dispatchId={purgeTarget?.id}
        dispatchNumber={purgeTarget?.dispatch_number}
        onClose={() => setPurgeTarget(null)}
        onPurged={() => loadDispatches()}
      />

      <InvoicePreviewDialog
        open={!!aiqPreviewDispatch}
        onOpenChange={(v) => { if (!v) setAiqPreviewDispatch(null); }}
        dispatch={aiqPreviewDispatch}
        onSent={() => loadDispatches()}
      />

      <AldiBranchDialog
        open={aldiBranchOpen}
        onClose={() => setAldiBranchOpen(false)}
        dispatchId={aldiBranchDispatchId}
      />

      {/* Uplift Date Dialog */}
      <Dialog open={upliftDialogOpen} onOpenChange={(open) => { if (!open) { setUpliftDialogOpen(false); setUpliftDispatchRef(null); } }}>
        <DialogContent className="sm:max-w-[400px]" data-testid="uplift-date-dialog">
          <DialogHeader>
            <DialogTitle>Set Uplift Date</DialogTitle>
            <DialogDescription>When will this order be collected/uplifted?</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <DatePicker
              value={upliftDate}
              onChange={setUpliftDate}
              placeholder="Select uplift date"
              data-testid="input-uplift-date"
            />
            {upliftDispatchRef?.dispatch_date && (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setUpliftDate(upliftDispatchRef.dispatch_date)}
                data-testid="btn-same-as-dispatch"
              >
                Same as Dispatch Date ({upliftDispatchRef.dispatch_date})
              </Button>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUpliftDialogOpen(false); setUpliftDispatchRef(null); }}>Cancel</Button>
            <Button onClick={confirmMarkDispatched} data-testid="btn-confirm-dispatch">Confirm Dispatch</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Stock Return */}
      <RecordReturnDialog
        dispatch={returnDispatch}
        open={returnDialogOpen}
        onOpenChange={setReturnDialogOpen}
        onSuccess={() => loadData()}
      />

      {/* Reverse Dispatch (order cancelled) */}
      <Dialog open={reverseDialogOpen} onOpenChange={(open) => { if (!open) { setReverseDialogOpen(false); setReverseTarget(null); } }}>
        <DialogContent className="sm:max-w-[480px]" data-testid="reverse-dispatch-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <RotateCcw className="w-5 h-5" /> Reverse Dispatch #{reverseTarget?.dispatch_number}
            </DialogTitle>
            <DialogDescription>
              For orders cancelled after being assembled/dispatched. All remaining stock is
              formally recorded as returned to main store (numbered Return Note), and the
              dispatch is marked <strong>Reversed</strong> — kept in history but excluded from
              sales reports and AccountsIQ.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            <Label htmlFor="reverseReason">Reason *</Label>
            <Textarea
              id="reverseReason"
              value={reverseReason}
              onChange={(e) => setReverseReason(e.target.value)}
              placeholder="e.g. Order cancelled by customer after assembly"
              rows={2}
              maxLength={500}
              data-testid="reverse-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReverseDialogOpen(false)} disabled={reversing}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleReverse}
              disabled={reversing || reverseReason.trim().length < 5}
              data-testid="confirm-reverse-btn"
            >
              {reversing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reversing...</>
              ) : (
                <><RotateCcw className="w-4 h-4 mr-2" /> Reverse Dispatch</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Undo Reversal (admin) */}
      <Dialog open={!!undoTarget} onOpenChange={(open) => { if (!open) setUndoTarget(null); }}>
        <DialogContent className="sm:max-w-[480px]" data-testid="undo-reverse-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-blue-700" /> Undo Reversal — #{undoTarget?.dispatch_number}
            </DialogTitle>
            <DialogDescription>
              For reversals made in error. The reversal return record is voided (kept in the
              register, watermarked VOID) and the dispatch is restored to its previous status —
              one consistent step, fully audit-logged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-1">
            <Label htmlFor="undoReason">Reason *</Label>
            <Textarea
              id="undoReason" rows={2} maxLength={500}
              value={undoReason}
              onChange={(e) => setUndoReason(e.target.value)}
              placeholder="e.g. Wrong order reversed — customer did not cancel"
              data-testid="undo-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUndoTarget(null)} disabled={undoing}>Cancel</Button>
            <Button
              onClick={handleUndoReverse}
              disabled={undoing || undoReason.trim().length < 5}
              data-testid="confirm-undo-reverse-btn"
            >
              {undoing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Restoring...</>
              ) : (
                <><RotateCcw className="w-4 h-4 mr-2" /> Undo Reversal</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
